import { createFileRoute } from "@tanstack/react-router"
import { ShelfiIcon, ShelfiLogo } from "@/components/ShelfiLogo"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"

export const Route = createFileRoute("/design/logo-showcase")({
  component: LogoShowcase,
})

function LogoShowcase() {
  return (
    <div className="container mx-auto space-y-8 py-8">
      <div className="space-y-2">
        <h1 className="text-4xl font-bold">SHELFI Logo Showcase</h1>
        <p className="text-muted-foreground">
          A comprehensive display of all logo variations, sizes, and states
        </p>
      </div>

      {/* Primary Variants */}
      <Card>
        <CardHeader>
          <CardTitle>Primary Logo</CardTitle>
          <CardDescription>
            Main logo with integrated shelf lines representing optimization layers
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-8">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
            <div className="space-y-4">
              <h3 className="text-muted-foreground text-sm font-medium">Light Mode</h3>
              <div className="space-y-4 rounded-lg border bg-white p-8">
                <div className="flex items-center justify-center">
                  <ShelfiLogo variant="primary" size="xl" />
                </div>
                <div className="flex items-center justify-center">
                  <ShelfiLogo variant="primary" size="lg" />
                </div>
                <div className="flex items-center justify-center">
                  <ShelfiLogo variant="primary" size="md" />
                </div>
                <div className="flex items-center justify-center">
                  <ShelfiLogo variant="primary" size="sm" />
                </div>
              </div>
            </div>
            <div className="space-y-4">
              <h3 className="text-muted-foreground text-sm font-medium">Dark Mode</h3>
              <div className="space-y-4 rounded-lg border bg-gray-900 p-8">
                <div className="flex items-center justify-center">
                  <ShelfiLogo variant="primary" size="xl" />
                </div>
                <div className="flex items-center justify-center">
                  <ShelfiLogo variant="primary" size="lg" />
                </div>
                <div className="flex items-center justify-center">
                  <ShelfiLogo variant="primary" size="md" />
                </div>
                <div className="flex items-center justify-center">
                  <ShelfiLogo variant="primary" size="sm" />
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Motion Variants */}
      <Card>
        <CardHeader>
          <CardTitle>Motion Logo</CardTitle>
          <CardDescription>
            Dynamic variant with diagonal motion lines inspired by ACTION&apos;s design language
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-8">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <h3 className="text-muted-foreground text-sm font-medium">Static</h3>
                <Badge variant="outline">No Animation</Badge>
              </div>
              <div className="space-y-4 rounded-lg border bg-gray-50 p-8">
                <div className="flex items-center justify-center">
                  <ShelfiLogo variant="motion" size="lg" />
                </div>
                <div className="flex items-center justify-center">
                  <ShelfiLogo variant="motion" size="md" />
                </div>
              </div>
            </div>
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <h3 className="text-muted-foreground text-sm font-medium">Animated</h3>
                <Badge>With Animation</Badge>
              </div>
              <div className="space-y-4 rounded-lg border bg-gray-50 p-8">
                <div className="flex items-center justify-center">
                  <ShelfiLogo variant="motion" size="lg" animated />
                </div>
                <div className="flex items-center justify-center">
                  <ShelfiLogo variant="motion" size="md" animated />
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Icon Variants */}
      <Card>
        <CardHeader>
          <CardTitle>Icon Mark</CardTitle>
          <CardDescription>
            Simplified shelf grid for app icons and small-scale usage
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-8 md:grid-cols-4">
            <div className="space-y-4">
              <h3 className="text-muted-foreground text-sm font-medium">Large Icon</h3>
              <div className="flex items-center justify-center rounded-lg border bg-gray-50 p-4">
                <ShelfiLogo variant="icon" size="lg" />
              </div>
            </div>
            <div className="space-y-4">
              <h3 className="text-muted-foreground text-sm font-medium">Medium Icon</h3>
              <div className="flex items-center justify-center rounded-lg border bg-gray-50 p-4">
                <ShelfiLogo variant="icon" size="md" />
              </div>
            </div>
            <div className="space-y-4">
              <h3 className="text-muted-foreground text-sm font-medium">Small Icons</h3>
              <div className="flex items-center justify-center gap-2 rounded-lg border bg-gray-50 p-4">
                <ShelfiIcon size={32} className="text-[#002B87]" />
                <ShelfiIcon size={24} className="text-[#002B87]" />
                <ShelfiIcon size={16} className="text-[#002B87]" />
              </div>
            </div>
            <div className="space-y-4">
              <h3 className="text-muted-foreground text-sm font-medium">Dark Mode</h3>
              <div className="flex items-center justify-center gap-2 rounded-lg border bg-gray-900 p-4">
                <ShelfiIcon size={32} className="text-[#4FC3F7]" />
                <ShelfiIcon size={24} className="text-[#4FC3F7]" />
                <ShelfiIcon size={16} className="text-[#4FC3F7]" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Loading States */}
      <Card>
        <CardHeader>
          <CardTitle>Loading & Interactive States</CardTitle>
          <CardDescription>
            Animated states for loading indicators and user interactions
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-8">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            <div className="space-y-4">
              <h3 className="text-muted-foreground text-sm font-medium">Loading Animation</h3>
              <div className="rounded-lg border bg-gray-50 p-8">
                <div className="flex items-center justify-center">
                  <ShelfiLogo variant="primary" size="md" animated />
                </div>
                <p className="text-muted-foreground mt-4 text-center text-sm">
                  Shelf scanning effect
                </p>
              </div>
            </div>
            <div className="space-y-4">
              <h3 className="text-muted-foreground text-sm font-medium">Hover State</h3>
              <div className="rounded-lg border bg-gray-50 p-8">
                <div className="flex items-center justify-center">
                  <ShelfiLogo
                    variant="primary"
                    size="md"
                    className="cursor-pointer transition-transform hover:scale-105"
                  />
                </div>
                <p className="text-muted-foreground mt-4 text-center text-sm">
                  Hover for glow effect
                </p>
              </div>
            </div>
            <div className="space-y-4">
              <h3 className="text-muted-foreground text-sm font-medium">Motion Entry</h3>
              <div className="rounded-lg border bg-gray-50 p-8">
                <div className="flex items-center justify-center">
                  <ShelfiLogo variant="motion" size="md" animated />
                </div>
                <p className="text-muted-foreground mt-4 text-center text-sm">Slide-in animation</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Usage Examples */}
      <Card>
        <CardHeader>
          <CardTitle>Usage Examples</CardTitle>
          <CardDescription>Logo applications in different UI contexts</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4">
            <h3 className="text-muted-foreground text-sm font-medium">Navigation Header</h3>
            <div className="rounded-lg border bg-white p-4">
              <div className="flex items-center justify-between">
                <ShelfiLogo variant="motion" size="sm" />
                <nav className="flex gap-6">
                  <button type="button" className="text-sm font-medium">
                    Projects
                  </button>
                  <button type="button" className="text-sm font-medium">
                    Settings
                  </button>
                  <button type="button" className="text-sm font-medium">
                    Help
                  </button>
                </nav>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-muted-foreground text-sm font-medium">App Icon Usage</h3>
            <div className="flex gap-4">
              <div className="rounded-xl bg-gray-100 p-3">
                <ShelfiIcon size={48} className="text-[#002B87]" />
              </div>
              <div className="rounded-xl bg-[#002B87] p-3">
                <ShelfiIcon size={48} className="text-white" />
              </div>
              <div className="rounded-xl bg-gradient-to-br from-[#002B87] to-[#00A3E0] p-3">
                <ShelfiIcon size={48} className="text-white" />
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-muted-foreground text-sm font-medium">Loading Screen</h3>
            <div className="rounded-lg bg-gray-900 p-12">
              <div className="flex flex-col items-center justify-center space-y-4">
                <ShelfiLogo variant="primary" size="lg" animated />
                <p className="text-sm text-white">Optimizing shelf layouts...</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Color Reference */}
      <Card>
        <CardHeader>
          <CardTitle>Brand Colors</CardTitle>
          <CardDescription>
            Color palette derived from ACTION&apos;s retail branding
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <div className="space-y-2">
              <div className="h-24 rounded-lg bg-[#002B87]"></div>
              <div className="space-y-1">
                <p className="text-sm font-medium">Deep Retail Blue</p>
                <p className="text-muted-foreground text-xs">#002B87</p>
              </div>
            </div>
            <div className="space-y-2">
              <div className="h-24 rounded-lg bg-[#00A3E0]"></div>
              <div className="space-y-1">
                <p className="text-sm font-medium">Bright Action Blue</p>
                <p className="text-muted-foreground text-xs">#00A3E0</p>
              </div>
            </div>
            <div className="space-y-2">
              <div className="h-24 rounded-lg bg-[#4FC3F7]"></div>
              <div className="space-y-1">
                <p className="text-sm font-medium">Light Sky Blue</p>
                <p className="text-muted-foreground text-xs">#4FC3F7</p>
              </div>
            </div>
            <div className="space-y-2">
              <div className="h-24 rounded-lg bg-gradient-to-r from-[#00A3E0] to-[#4FC3F7]"></div>
              <div className="space-y-1">
                <p className="text-sm font-medium">Shelf Gradient</p>
                <p className="text-muted-foreground text-xs">Linear gradient</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
